import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock3,
  Loader,
  MessageCircle,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldX,
  Trash2,
  User,
} from 'lucide-react';
import {
  getCompraAINotificationContacts,
  getWhatsappAccessNumbers,
  saveCompraAINotificationContacts,
  updateWhatsappAccessNumberStatus,
} from '../services/managementService';
import type {
  CompraAINotificationContact,
  WhatsappAccessNumber,
  WhatsappAccessScope,
} from '../types/management';

function isActive(value: WhatsappAccessNumber['is_actived']): boolean {
  return value === true || value === 1;
}

function formatPhoneForDisplay(phoneNumber?: string | null): string {
  const digits = (phoneNumber || '').replace(/\D/g, '');

  if (!digits) return '-';

  if (digits.startsWith('55') && digits.length >= 12) {
    const ddd = digits.slice(2, 4);
    const part1 = digits.slice(4, 9);
    const part2 = digits.slice(9);
    return `+55 ${ddd} ${part1}-${part2}`;
  }

  if (digits.length > 6) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}-${digits.slice(8)}`;
  }

  return `+${digits}`;
}

function formatCreatedAt(value?: string): string {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function normalizePhone(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, '');
}

export default function WhatsAppAccessControlTab() {
  const [numbers, setNumbers] = useState<WhatsappAccessNumber[]>([]);
  const [notificationContacts, setNotificationContacts] = useState<CompraAINotificationContact[]>([]);
  const [scope, setScope] = useState<WhatsappAccessScope>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationName, setNotificationName] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  const [isSavingNotification, setIsSavingNotification] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notificationError, setNotificationError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getWhatsappAccessNumbers(scope);
      setNumbers(data);
    } catch (err) {
      console.error('Error fetching WhatsApp access numbers:', err);
      setError('Erro ao carregar usuarios de WhatsApp.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const fetchNotificationContacts = async () => {
    setIsLoadingNotifications(true);
    setNotificationError(null);

    try {
      const data = await getCompraAINotificationContacts();
      setNotificationContacts(data);
    } catch (err) {
      console.error('Error fetching Compra AI notification contacts:', err);
      setNotificationError('Erro ao carregar numeros de notificacao.');
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  useEffect(() => {
    fetchNotificationContacts();
  }, []);

  const filteredNumbers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const digits = searchTerm.replace(/\D/g, '');

    if (!search && !digits) return numbers;

    return numbers.filter((item) => {
      const ownerName = item.owner_name?.toLowerCase() || '';
      const lid = item.lid?.toLowerCase() || '';
      const cellphone = item.cellphone || '';

      return (
        ownerName.includes(search) ||
        lid.includes(search) ||
        cellphone.includes(digits) ||
        formatPhoneForDisplay(cellphone).toLowerCase().includes(search)
      );
    });
  }, [numbers, searchTerm]);

  const pendingCount = numbers.filter((item) => !isActive(item.is_actived)).length;
  const activeCount = numbers.filter((item) => isActive(item.is_actived)).length;

  const handleToggleStatus = async (item: WhatsappAccessNumber) => {
    const nextStatus = !isActive(item.is_actived);
    setUpdatingId(item.id_whatsapp_number);
    setError(null);

    try {
      await updateWhatsappAccessNumberStatus(item.id_whatsapp_number, nextStatus);

      setNumbers((current) => {
        if (scope === 'pending' && nextStatus) {
          return current.filter((row) => row.id_whatsapp_number !== item.id_whatsapp_number);
        }

        return current.map((row) =>
          row.id_whatsapp_number === item.id_whatsapp_number
            ? { ...row, is_actived: nextStatus ? 1 : 0 }
            : row
        );
      });
    } catch (err) {
      console.error('Error updating WhatsApp access number:', err);
      setError('Erro ao atualizar acesso do usuario.');
    } finally {
      setUpdatingId(null);
    }
  };

  const persistNotificationContacts = async (contacts: CompraAINotificationContact[]) => {
    setIsSavingNotification(true);
    setNotificationError(null);

    try {
      const data = await saveCompraAINotificationContacts(contacts);
      setNotificationContacts(data);
      setNotificationName('');
      setNotificationPhone('');
    } catch (err) {
      console.error('Error saving Compra AI notification contacts:', err);
      setNotificationError('Erro ao salvar numeros de notificacao.');
    } finally {
      setIsSavingNotification(false);
    }
  };

  const handleAddNotificationContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const name = notificationName.trim();
    const phoneNumber = normalizePhone(notificationPhone);

    if (!name) {
      setNotificationError('Informe o nome da pessoa que recebera a notificacao.');
      return;
    }

    if (phoneNumber.length < 12) {
      setNotificationError('Informe o WhatsApp com DDI, DDD e telefone. Exemplo: +551190000-0000.');
      return;
    }

    if (notificationContacts.some((contact) => contact.phoneNumber === phoneNumber)) {
      setNotificationError('Esse numero ja esta cadastrado para notificacoes.');
      return;
    }

    await persistNotificationContacts([
      ...notificationContacts,
      {
        id: `${phoneNumber}-${Date.now()}`,
        name,
        phoneNumber,
      },
    ]);
  };

  const handleRemoveNotificationContact = async (contactId: string) => {
    await persistNotificationContacts(
      notificationContacts.filter((contact) => contact.id !== contactId)
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600">Carregando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Compra AI
              </h2>
              <p className="text-sm text-gray-500">
                Numeros liberados e aguardando validacao para conversar com o agente
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2">
              <p className="text-xs font-medium text-amber-700">Pendentes</p>
              <p className="text-lg font-semibold text-amber-900">{pendingCount}</p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2">
              <p className="text-xs font-medium text-emerald-700">Liberados</p>
              <p className="text-lg font-semibold text-emerald-900">{activeCount}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Notificacoes de solicitacao
              </h3>
              <p className="text-sm text-gray-500">
                Esses numeros recebem aviso quando alguem pedir acesso ao Compra AI
              </p>
            </div>
          </div>

          <form onSubmit={handleAddNotificationContact} className="grid w-full gap-3 xl:max-w-3xl sm:grid-cols-[1fr_220px_auto]">
            <input
              type="text"
              value={notificationName}
              onChange={(event) => setNotificationName(event.target.value)}
              placeholder="Nome"
              disabled={isSavingNotification}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:bg-gray-100 transition-colors"
            />
            <input
              type="tel"
              value={notificationPhone}
              onChange={(event) => setNotificationPhone(event.target.value)}
              placeholder="+551190000-0000"
              disabled={isSavingNotification}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:bg-gray-100 transition-colors"
            />
            <button
              type="submit"
              disabled={isSavingNotification}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium disabled:opacity-60"
            >
              {isSavingNotification ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              <span>Adicionar</span>
            </button>
          </form>
        </div>

        {notificationError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{notificationError}</p>
          </div>
        )}

        <div className="mt-5">
          {isLoadingNotifications ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader className="w-4 h-4 animate-spin" />
              <span>Carregando numeros de notificacao...</span>
            </div>
          ) : notificationContacts.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhum numero cadastrado para receber notificacoes.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {notificationContacts.map((contact) => (
                <div key={contact.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{contact.name}</p>
                    <p className="text-sm text-gray-500">{formatPhoneForDisplay(contact.phoneNumber)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveNotificationContact(contact.id)}
                    disabled={isSavingNotification}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-60"
                    title="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="text-red-600 hover:text-red-800 transition-colors"
            title="Atualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, numero ou LID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'pending', label: 'Aguardando validacao' },
              ].map((option) => {
                const isSelected = scope === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setScope(option.value as WhatsappAccessScope)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isSelected
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={fetchData}
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Atualizar</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredNumbers.length === 0 ? (
          <div className="p-12 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum usuario encontrado' : 'Nenhum usuario para exibir'}
            </h3>
            <p className="text-gray-500">
              {scope === 'pending'
                ? 'Nao ha usuarios pendentes de liberacao.'
                : 'Nao ha contas cadastradas para esta listagem.'}
            </p>
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Numero
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      LID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acoes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredNumbers.map((item) => {
                    const active = isActive(item.is_actived);
                    const isUpdating = updatingId === item.id_whatsapp_number;

                    return (
                      <tr key={item.id_whatsapp_number} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.owner_name || 'Sem nome'}
                              </p>
                              <p className="text-xs text-gray-500">ID {item.id_whatsapp_number}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {formatPhoneForDisplay(item.cellphone)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="block max-w-[240px] truncate text-sm text-gray-600" title={item.lid || ''}>
                            {item.lid || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatCreatedAt(item.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                            active
                              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                              : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                          }`}>
                            {active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock3 className="w-3.5 h-3.5" />}
                            {active ? 'Liberado' : 'Pendente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(item)}
                            disabled={isUpdating}
                            className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${
                              active
                                ? 'border border-red-200 text-red-700 hover:bg-red-50'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {isUpdating ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : active ? (
                              <ShieldX className="w-4 h-4" />
                            ) : (
                              <ShieldCheck className="w-4 h-4" />
                            )}
                            <span>{active ? 'Bloquear' : 'Liberar'}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden divide-y divide-gray-200">
              {filteredNumbers.map((item) => {
                const active = isActive(item.is_actived);
                const isUpdating = updatingId === item.id_whatsapp_number;

                return (
                  <div key={item.id_whatsapp_number} className="p-4 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-gray-900">{item.owner_name || 'Sem nome'}</p>
                        <p className="text-sm text-gray-500">{formatPhoneForDisplay(item.cellphone)}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                        active
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                          : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                      }`}>
                        {active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock3 className="w-3.5 h-3.5" />}
                        {active ? 'Liberado' : 'Pendente'}
                      </span>
                    </div>

                    <div className="grid gap-2 text-sm text-gray-600">
                      <p>
                        <span className="font-medium text-gray-700">LID:</span> {item.lid || '-'}
                      </p>
                      <p>
                        <span className="font-medium text-gray-700">Criado em:</span> {formatCreatedAt(item.created_at)}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleToggleStatus(item)}
                      disabled={isUpdating}
                      className={`inline-flex w-full items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${
                        active
                          ? 'border border-red-200 text-red-700 hover:bg-red-50'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isUpdating ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : active ? (
                        <ShieldX className="w-4 h-4" />
                      ) : (
                        <ShieldCheck className="w-4 h-4" />
                      )}
                      <span>{active ? 'Bloquear acesso' : 'Liberar acesso'}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <p className="text-sm text-gray-500 text-center">
        {filteredNumbers.length} {filteredNumbers.length === 1 ? 'usuario exibido' : 'usuarios exibidos'}
      </p>
    </div>
  );
}
